import { CalendarCheck, Timer, Trophy, Flame } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { GlassCard } from "@/components/GlassCard";
import { useLanguage } from "@/i18n/LanguageContext";

// Stable keys for skeleton placeholders (not array indices)
const SKELETON_KEYS = ["skeleton-activities", "skeleton-minutes", "skeleton-best-day", "skeleton-streak"];

const StatCard = ({ icon: Icon, iconClass, label, value, unit, testId }) => (
  <GlassCard className="p-4">
    <div className="flex items-center gap-2 text-[hsl(var(--muted-foreground))]">
      <Icon size={14} className={iconClass} />
      <span className="text-xs">{label}</span>
    </div>
    <p
      data-testid={testId}
      className="mt-2 text-xl font-semibold font-heading [font-variant-numeric:tabular-nums] truncate"
    >
      {value}
      {unit && (
        <span className="text-sm text-[hsl(var(--muted-foreground))] font-normal ml-1">
          {unit}
        </span>
      )}
    </p>
  </GlassCard>
);

export const StatsGrid = ({ stats, loading }) => {
  const { t, locale } = useLanguage();

  if (loading) {
    return (
      <div className="grid grid-cols-2 gap-3">
        {SKELETON_KEYS.map((key) => (
          <Skeleton key={key} className="h-24 rounded-2xl bg-white/10" />
        ))}
      </div>
    );
  }

  // Localize the best-day weekday from its ISO date (backend label is English-only)
  const bestDayLabel = stats.best_day
    ? new Date(`${stats.best_day.date}T00:00:00`).toLocaleDateString(locale, {
        weekday: "long",
      })
    : "\u2014";

  return (
    <div className="grid grid-cols-2 gap-3">
      <StatCard
        icon={CalendarCheck}
        iconClass="text-cyan-300/80"
        label={t("progress.activitiesThisWeek")}
        value={stats.activities_this_week}
        testId="progress-activities-week"
      />
      <StatCard
        icon={Timer}
        iconClass="text-cyan-300/80"
        label={t("progress.totalMinutes")}
        value={stats.weekly_minutes}
        unit={t("common.min")}
        testId="progress-total-minutes"
      />
      <StatCard
        icon={Trophy}
        iconClass="text-amber-400/80"
        label={t("progress.bestDay")}
        value={bestDayLabel}
        unit={stats.best_day ? `${stats.best_day.minutes} ${t("common.min")}` : null}
        testId="progress-best-day"
      />
      <StatCard
        icon={Flame}
        iconClass="text-amber-400/80"
        label={t("progress.currentStreak")}
        value={stats.current_streak}
        unit={stats.current_streak === 1 ? t("common.day") : t("common.days")}
        testId="progress-streak"
      />
    </div>
  );
};
